import { useIonRouter } from '@ionic/react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import Segment from '../../Segment'

export type InboxTab = 'activities' | 'chats' | 'requests'

const ORDER: InboxTab[] = ['activities', 'chats', 'requests']

const ROUTES: Record<InboxTab, string> = {
    activities: '/inbox',
    chats: '/inbox/chats',
    requests: '/inbox/requests',
}

interface InboxTabsProps {
    active: InboxTab
    /** Offene Anfragen. Steht als Zahl am Reiter, nicht als stiller Punkt. */
    requestCount?: number
}

/**
 * Der Umschalter des Postfachs.
 *
 * Aktivitaeten, Unterhaltungen und Anfragen beantworten dieselbe Frage — "gibt
 * es etwas Neues fuer mich?" — und liegen deshalb hinter einem Reiter der
 * Navigation. Sie bleiben aber drei getrennte Listen: Aktivitaeten sind
 * einseitige Ereignisse mit einem gemeinsamen Gelesen-Zeitpunkt, Unterhaltungen
 * dagegen laufende Faeden mit je eigenem Zaehler. In einer Liste vermischt
 * ginge eines der beiden Modelle verloren, und ein laufender Chat saecke unter
 * den Benachrichtigungen weg.
 *
 * Jeder Reiter ist eine eigene Route und damit eine eigene Seite. Das ist kein
 * Zufall: Die Aktivitaeten merken sich ihren Gelesen-Zeitpunkt beim Verlassen
 * der Seite (`useIonViewWillLeave`). Als bloss umgeschalteter Zustand innerhalb
 * einer Seite wuerden sie auch dann als gelesen gelten, wenn man sie nie
 * gesehen hat.
 */
const InboxTabs: React.FC<InboxTabsProps> = ({ active, requestCount }) => {
    const { t } = useTranslation()
    const router = useIonRouter()

    const onChange = useCallback(
        (value?: string | null) => {
            const next = ORDER[Number(value)] ?? 'activities'
            if (next === active) return

            router.push(ROUTES[next], 'none', 'replace')
        },
        [active, router],
    )

    const requests = t('contact.tabs.requests', 'Anfragen')

    return (
        <Segment
            value={`${ORDER.indexOf(active)}`}
            onIonChange={(event) => onChange(event.detail.value as string)}
            tabs={[
                { tab: t('inbox.tabs.activities', 'Aktivitäten') },
                { tab: t('contact.tabs.chats', 'Chats') },
                {
                    tab: requestCount
                        ? `${requests} (${requestCount})`
                        : requests,
                },
            ]}
        />
    )
}

export default InboxTabs
